import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaChartPie, FaFilter, FaSearch, FaChevronDown, FaChevronUp, FaMoneyBillWave, FaChartLine, FaPercentage } from 'react-icons/fa';
import { API_URL } from '../api/api';
import { useCompetence } from '../contexts/CompetenceContext';
import {
    Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem,
    Button, CircularProgress, Alert, Card, CardContent, Tabs, Tab, Chip, Stack,
    useTheme, alpha
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const ProductionAnalysisPage = () => {
    const theme = useTheme();
    const { globalCompetence } = useCompetence();

    const [competencias, setCompetencias] = useState([]);
    const [competencia, setCompetencia] = useState('');
    const [analysisData, setAnalysisData] = useState({
        items: [],
        by_account: [],
        by_item: [],
        by_specialty: [],
        by_doctor: [],
        by_character: [],
        by_type: [],
        summary: {}
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        fetchCompetencias();
    }, []);

    // Sync with global competence
    useEffect(() => {
        if (globalCompetence && competencias.length > 0 && !competencia) {
            if (competencias.includes(globalCompetence)) {
                setCompetencia(globalCompetence);
            }
        }
    }, [globalCompetence, competencias]);

    useEffect(() => {
        if (competencia) {
            fetchAnalysis();
        } else {
            setAnalysisData({ items: [], by_account: [], by_item: [], summary: {} });
        }
    }, [competencia]);

    const fetchCompetencias = async () => {
        try {
            const res = await axios.get(`${API_URL}/production/competencias`);
            const data = res.data;
            setCompetencias(data);
            // Use global competence if available
            if (data.length > 0 && !competencia) {
                const initial = globalCompetence && data.includes(globalCompetence) ? globalCompetence : data[0];
                setCompetencia(initial);
            }
        } catch (error) {
            console.error("Erro ao buscar competências:", error);
            setError("Erro ao carregar competências.");
        }
    };

    const fetchAnalysis = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${API_URL}/production/analysis`, {
                params: { competencia }
            });
            setAnalysisData(res.data);
        } catch (err) {
            console.error("Erro ao buscar análise:", err);
            setError("Erro ao carregar dados de análise. Por favor, tente novamente.");
            setAnalysisData({ items: [], by_account: [], by_item: [], summary: {} });
        } finally {
            setLoading(false);
        }
    };

    // --- Chart Data Preparation ---
    const getTopProfitByAccount = () => {
        const sorted = [...analysisData.by_account].sort((a, b) => b.lucro - a.lucro).slice(0, 10);
        return {
            labels: sorted.map(item => item.conta),
            datasets: [
                {
                    label: 'Lucro (R$)',
                    data: sorted.map(item => item.lucro),
                    backgroundColor: alpha(theme.palette.secondary.main, 0.6),
                },
            ],
        };
    };

    const getTopProfitByItem = () => {
        const sorted = [...analysisData.by_item].sort((a, b) => b.lucro - a.lucro).slice(0, 10);
        return {
            labels: sorted.map(item => item.descricao.substring(0, 20) + '...'),
            datasets: [
                {
                    label: 'Lucro (R$)',
                    data: sorted.map(item => item.lucro),
                    backgroundColor: alpha(theme.palette.primary.main, 0.6),
                },
            ],
        };
    };

    const getTopProfitBySpecialty = () => {
        const sorted = [...analysisData.by_specialty].sort((a, b) => b.lucro - a.lucro).slice(0, 10);
        return {
            labels: sorted.map(item => item.especialidade),
            datasets: [
                {
                    label: 'Lucro (R$)',
                    data: sorted.map(item => item.lucro),
                    backgroundColor: alpha(theme.palette.success.main, 0.6),
                },
            ],
        };
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: false },
        },
        maintainAspectRatio: false,
    };

    // --- Columns Definitions ---
    const currencyFormatter = (params) => {
        if (params.value == null) return '';
        return `R$ ${params.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const percentFormatter = (params) => {
        if (params.value == null) return '';
        return `${params.value.toFixed(2)}%`;
    };

    const profitCellRenderer = (params) => {
        if (params.value == null) return '';
        const color = params.value >= 0 ? 'success.main' : 'error.main';
        return (
            <Typography variant="body2" sx={{ color: color, fontWeight: 'bold' }}>
                {currencyFormatter(params)}
            </Typography>
        );
    };

    const marginCellRenderer = (params) => {
        if (params.value == null) return '';
        const color = params.value >= 0 ? 'success' : 'error';
        return (
            <Chip
                label={percentFormatter(params)}
                color={color}
                size="small"
                variant="outlined"
            />
        );
    };

    const commonColumns = [
        {
            field: 'custo_total', headerName: 'Custo Total', width: 150, type: 'number',
            valueFormatter: currencyFormatter
        },
        {
            field: 'lucro', headerName: 'Lucro', width: 150, type: 'number',
            renderCell: profitCellRenderer
        },
        {
            field: 'margem', headerName: 'Margem %', width: 120, type: 'number',
            renderCell: marginCellRenderer
        }
    ];

    const columns = [
        { field: 'atendimento', headerName: 'Atendimento', width: 100 },
        { field: 'convenio', headerName: 'Convênio', width: 120 },
        { field: 'especialidade', headerName: 'Especialidade', width: 150 },
        { field: 'medico', headerName: 'Médico', width: 150 },
        { field: 'procedimento_principal', headerName: 'Proc. Principal', width: 150 },
        { field: 'carater', headerName: 'Caráter', width: 120 },
        { field: 'tipo_atendimento', headerName: 'Tipo Atend.', width: 120 },
        { field: 'qtd_itens', headerName: 'Qtd Itens', width: 100, type: 'number' },
        ...commonColumns
    ];

    const columnsAccount = [
        { field: 'conta', headerName: 'Conta', width: 250 },
        { field: 'qtd', headerName: 'Qtd Total', width: 120, type: 'number' },
        { field: 'receita', headerName: 'Receita Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'custo', headerName: 'Custo Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'lucro', headerName: 'Lucro', width: 150, type: 'number', renderCell: profitCellRenderer },
        { field: 'margem', headerName: 'Margem %', width: 120, type: 'number', renderCell: marginCellRenderer }
    ];

    const columnsItem = [
        { field: 'codigo', headerName: 'Código', width: 120 },
        { field: 'descricao', headerName: 'Descrição', width: 300 },
        { field: 'qtd', headerName: 'Qtd Total', width: 120, type: 'number' },
        { field: 'receita', headerName: 'Receita Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'custo', headerName: 'Custo Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'lucro', headerName: 'Lucro', width: 150, type: 'number', renderCell: profitCellRenderer },
        { field: 'margem', headerName: 'Margem %', width: 120, type: 'number', renderCell: marginCellRenderer }
    ];

    const columnsSpecialty = [
        { field: 'especialidade', headerName: 'Especialidade', width: 250 },
        { field: 'qtd', headerName: 'Qtd Total', width: 120, type: 'number' },
        { field: 'receita', headerName: 'Receita Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'custo', headerName: 'Custo Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'lucro', headerName: 'Lucro', width: 150, type: 'number', renderCell: profitCellRenderer },
        { field: 'margem', headerName: 'Margem %', width: 120, type: 'number', renderCell: marginCellRenderer }
    ];

    const columnsDoctor = [
        { field: 'medico', headerName: 'Médico', width: 250 },
        { field: 'qtd', headerName: 'Qtd Total', width: 120, type: 'number' },
        { field: 'receita', headerName: 'Receita Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'custo', headerName: 'Custo Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'lucro', headerName: 'Lucro', width: 150, type: 'number', renderCell: profitCellRenderer },
        { field: 'margem', headerName: 'Margem %', width: 120, type: 'number', renderCell: marginCellRenderer }
    ];

    const columnsCharacter = [
        { field: 'carater', headerName: 'Caráter', width: 250 },
        { field: 'qtd', headerName: 'Qtd Total', width: 120, type: 'number' },
        { field: 'receita', headerName: 'Receita Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'custo', headerName: 'Custo Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'lucro', headerName: 'Lucro', width: 150, type: 'number', renderCell: profitCellRenderer },
        { field: 'margem', headerName: 'Margem %', width: 120, type: 'number', renderCell: marginCellRenderer }
    ];

    const columnsType = [
        { field: 'tipo', headerName: 'Tipo Atendimento', width: 250 },
        { field: 'qtd', headerName: 'Qtd Total', width: 120, type: 'number' },
        { field: 'receita', headerName: 'Receita Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'custo', headerName: 'Custo Total', width: 150, type: 'number', valueFormatter: currencyFormatter },
        { field: 'lucro', headerName: 'Lucro', width: 150, type: 'number', renderCell: profitCellRenderer },
        { field: 'margem', headerName: 'Margem %', width: 120, type: 'number', renderCell: marginCellRenderer }
    ];

    const SummaryCard = ({ title, value, icon, color, subValue }) => (
        <Card sx={{ height: '100%', borderLeft: `4px solid ${color}`, bgcolor: 'background.paper' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                    <Box sx={{ color: color, fontSize: 24 }}>{icon}</Box>
                    <Typography color="textSecondary" variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                </Stack>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {value}
                </Typography>
                {subValue && (
                    <Typography variant="caption" color="textSecondary">
                        {subValue}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FaChartPie /> Análise de Produção
                </Typography>
                <Paper sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'background.paper' }}>
                    <FaFilter color={theme.palette.text.secondary} />
                    <Select
                        value={competencia}
                        onChange={(e) => setCompetencia(e.target.value)}
                        displayEmpty
                        variant="standard"
                        disableUnderline
                        sx={{ minWidth: 150 }}
                    >
                        <MenuItem value="" disabled>Selecione a Competência</MenuItem>
                        {competencias.map((comp) => (
                            <MenuItem key={comp} value={comp}>{comp}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="contained"
                        onClick={fetchAnalysis}
                        disabled={!competencia || loading}
                        sx={{ borderRadius: 2 }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Atualizar'}
                    </Button>
                </Paper>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {analysisData.items.length > 0 && (
                <>
                    {/* Summary Cards Grid */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                title="Total Atendimentos"
                                value={analysisData.summary.total_atendimentos}
                                icon={<FaChartLine />}
                                color={theme.palette.info.main}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                title="Receita Total"
                                value={analysisData.summary.receita_total_geral?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                icon={<FaMoneyBillWave />}
                                color={theme.palette.success.main}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                title="Custo Total"
                                value={analysisData.summary.custo_total_geral?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                icon={<FaMoneyBillWave />}
                                color={theme.palette.error.main}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                title="Lucro Total"
                                value={analysisData.summary.lucro_total_geral?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                icon={<FaPercentage />}
                                color={analysisData.summary.lucro_total_geral >= 0 ? theme.palette.success.main : theme.palette.error.main}
                            />
                        </Grid>
                    </Grid>

                    {/* Charts Section */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, height: 400, borderRadius: 2, bgcolor: 'background.paper' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Top 10 Rentabilidade por Conta</Typography>
                                <Box sx={{ height: 320 }}>
                                    <Bar data={getTopProfitByAccount()} options={chartOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, height: 400, borderRadius: 2, bgcolor: 'background.paper' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Top 10 Rentabilidade por Item</Typography>
                                <Box sx={{ height: 320 }}>
                                    <Bar data={getTopProfitByItem()} options={chartOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, height: 400, borderRadius: 2, bgcolor: 'background.paper' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Top 10 Rentabilidade por Especialidade</Typography>
                                <Box sx={{ height: 320 }}>
                                    <Bar data={getTopProfitBySpecialty()} options={chartOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Data Grid Section */}
                    <Paper sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                                <Tab label="Por Atendimento" sx={{ fontWeight: 'bold' }} />
                                <Tab label="Por Conta" sx={{ fontWeight: 'bold' }} />
                                <Tab label="Por Item" sx={{ fontWeight: 'bold' }} />
                                <Tab label="Por Especialidade" sx={{ fontWeight: 'bold' }} />
                                <Tab label="Por Médico" sx={{ fontWeight: 'bold' }} />
                                <Tab label="Por Caráter" sx={{ fontWeight: 'bold' }} />
                                <Tab label="Por Tipo" sx={{ fontWeight: 'bold' }} />
                            </Tabs>
                        </Box>
                        <Box sx={{ height: 600, width: '100%', p: 0 }}>
                            {tabValue === 0 && (
                                <DataGrid
                                    rows={analysisData.items}
                                    columns={columns}
                                    getRowId={(row) => `${row.atendimento}-${row.convenio}`}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                            {tabValue === 1 && (
                                <DataGrid
                                    rows={analysisData.by_account}
                                    columns={columnsAccount}
                                    getRowId={(row) => row.conta}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                            {tabValue === 2 && (
                                <DataGrid
                                    rows={analysisData.by_item}
                                    columns={columnsItem}
                                    getRowId={(row) => row.descricao}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                            {tabValue === 3 && (
                                <DataGrid
                                    rows={analysisData.by_specialty}
                                    columns={columnsSpecialty}
                                    getRowId={(row) => row.especialidade}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                            {tabValue === 4 && (
                                <DataGrid
                                    rows={analysisData.by_doctor}
                                    columns={columnsDoctor}
                                    getRowId={(row) => row.medico}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                            {tabValue === 5 && (
                                <DataGrid
                                    rows={analysisData.by_character}
                                    columns={columnsCharacter}
                                    getRowId={(row) => row.carater}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                            {tabValue === 6 && (
                                <DataGrid
                                    rows={analysisData.by_type}
                                    columns={columnsType}
                                    getRowId={(row) => row.tipo}
                                    pageSizeOptions={[50, 100]}
                                    disableRowSelectionOnClick
                                    sx={{ border: 'none' }}
                                />
                            )}
                        </Box>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default ProductionAnalysisPage;
